import React, { type PropsWithChildren, useCallback, useEffect, useState } from 'react';

import AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import {
	envType,
	originType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';

import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

export const ANALYTICS_CHANNEL = 'accountSetup';

type PRODUCT_TYPE = 'confluence' | 'marketing';
export type AnalyticsData = {
	additionalAttributes?: Record<string, string | boolean | any | null>;
	cloudId?: string | null;
	product: 'confluence' | 'marketing';
	userId?: string | null;
};
export type AnalyticsProviderProps = {
	analyticsClient?: AnalyticsWebClient | null;
	analyticsData: AnalyticsData;
};

const isSSR = () => typeof window === 'undefined' || typeof document === 'undefined';

/**
 * AnalyticsProvider component takes care of
    - Provide AnalyticsListener to child components.
    - Every analytic event payload will have eventType in it. It will call appropriate send*Event method of analyticsWebClient based on event type.
    - It also sets channel to `accountSetup`.
 * @param props
 * @param product either Confluence or WAC
 * @returns AnalyticsListener wrapping children.
 */
export const AnalyticsProvider = ({
	children,
	analyticsData,
	analyticsClient: providedAnalyticsClient = null,
}: PropsWithChildren<AnalyticsProviderProps>): React.JSX.Element => {
	const { product, cloudId, userId, additionalAttributes } = analyticsData;

	const [analyticsClient, setAnalyticsClient] = useState<AnalyticsWebClient | null>(null);

	const defaultOnEvent = useCallback(
		({ payload }: UIAnalyticsEvent) => {
			analyticsClient?.onEvent?.(ANALYTICS_CHANNEL, {
				...payload,
				attributes: {
					...additionalAttributes,
					...payload?.attributes,
					product,
				},
			});
		},
		[analyticsClient, additionalAttributes, product],
	);

	const wacUpdateEvent = useCallback(
		(event: UIAnalyticsEvent) => {
			event.update((payload) => ({
				...payload,
				attributes: {
					...additionalAttributes,
					...payload?.attributes,
					product,
				},
			}));
		},
		[product, additionalAttributes],
	);

	//When calling this from WAC, we see the event is fired twice.
	//Therefore, we are checking if the product is marketing to call the updateEvent,
	//which does not seem to be working in AFM when used.
	const onEvent = product === 'marketing' ? wacUpdateEvent : defaultOnEvent;

	useEffect(() => {
		//  Only setup analytics client for SPA rendering since analytics requires window to be defined
		if (!isSSR()) {
			setAnalyticsClient(
				getAnalyticsWebClient({
					product: product as PRODUCT_TYPE,
					cloudId,
					userId,
					analyticsClient: providedAnalyticsClient,
				}),
			);
		}
	}, [product, cloudId, userId, setAnalyticsClient, providedAnalyticsClient]);

	if (isSSR() || !analyticsClient) {
		return <>{children}</>;
	}

	return (
		<AnalyticsListener channel={ANALYTICS_CHANNEL} onEvent={onEvent}>
			{children}
		</AnalyticsListener>
	);
};

// Define default analytic-web-client environment config for now
const getAnalyticsEnv = (hostname: string) => {
	if (hostname.includes('localhost')) {
		return envType.LOCAL;
	}
	//  Confluence staging has jira-dev, WAC staging has stg
	if (hostname.includes('jira-dev') || hostname.includes('stg')) {
		return envType.STAGING;
	}

	return envType.PROD;
};

export const getAnalyticsWebClient = ({
	product,
	cloudId,
	userId,
	analyticsClient,
}: {
	analyticsClient?: AnalyticsWebClient | null;
	cloudId?: string | null;
	product: PRODUCT_TYPE;
	userId?: string | null;
}): AnalyticsWebClient => {
	const client =
		analyticsClient ??
		new AnalyticsWebClient({
			product: product,
			env: getAnalyticsEnv(window.location.hostname),
			origin: originType.WEB,
		});

	if (cloudId) {
		client.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	}
	if (userId) {
		client.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
	}

	return client;
};
